/* eslint-disable max-lines */
// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapActions, mapState } from "vuex";
import api from '../../../share/util/api/service';
import alert from "../../../share/alert";
import cstmTable from "../../../share/components/vue/PaginationTable";
import loading from "../../../share/components/vue/ProcessingIcon";
import fullModal from "../../../share/components/vue/FullModal";
import noTextSVGButton, { ICON_KEY, BUTTON_TYPE } from "../../../share/components/vue/NoTextSVGButton";
import WrappedTeleport from "../../../share/components/vue/WrappedTeleport";
import config from "../../../share/config";
import session from "../../../share/session";
import eventLogUtil from "../../../share/util/eventLogUtil";
import individualDiagnose from "../../../share/util/individualDiagnose";
import util from "../../../share/util/utils";
import customProgress from "../../../share/components/vue/CustomProgress";
import {
  RES_TYPE,
  ERR_KEY,
  I18N_PARENT_ID,
  PARAM,
  AT_EXE_TYPE
} from "../../pages/individualActiveTest/individualActiveTestConstants";
import atUtil from "../../pages/individualActiveTest/individualActiveTestUtil";
import memoPage from "../../vue/MemoPage";
import dataMonitor from "../../vue/dataMonitor/DataMonitor";
import controlPanel from "../../vue/individualActiveTest/activeTestControlPanel/ActiveTestControlPanel";
import dmUtil, { TAB } from "../dataMonitor/dataMonitorUtil";
import { createZoomTabHandlersForOptions, isResetZoomDisabled } from '../dataMonitor/dataMonitorZoomUtil';
import { CsvFileWriter, getMeasurementFileName } from '../dataMonitor/dataMonitorFileUtil';
import { exportCsv } from '../dataRecollection/dataMonitor/csv';

// 特殊キーのenum
const SPAT_KEY_LIST = {
  CTRL_ALT_I: ["1110101", "1260101"],
};
// 特殊キーに対応するcode
const SPAT_KEY_CODE = [{
  key: SPAT_KEY_LIST.CTRL_ALT_I,
  code: [17, 18, 73]
}];

export default {
  name: "individualActiveTest",
  data() {
    return {
      processing: false,
      showCsvProgress: false, // CSVエクスポートの進捗表示フラグ
      // システム間移動判定
      isUseCache: true,
      params: null,
      queryName: "",
      // 多言語対応
      menuResponse: null,
      // フラグ類
      showAtSelect: true, // アクティブテスト項目選択画面表示フラグ
      showDmSelect: true, // データモニタ項目選択画面表示フラグ
      inExecution: false, // ECU計測中フラグ
      inPlaying: false, // 計測データ再生中フラグ
      isDriving: false, // 駆動中フラグ
      isReadExecuted: false, // データ読み出し実行フラグ
      isAtStandby: false, // at_standby実行フラグ
      parentName: "active_test", // データモニタのカウントアップ処理は行わない
      // 選択項目データ
      columns: [], // 項目リストヘッダー
      originalFunctionList: [], // APIから取得した機能リスト
      filteredFunctionList: [], // 表示用の機能リスト
      selectedActiveTest: {}, // 選択中の機能リスト項目
      selectedAtFunc: {}, // 選択確定した機能、子コンポーネントに渡す
      i18nMenuTitleKey: "", // 選択確定項目の多言語対応キー
      // 項目一覧フィルタ機能
      filterText: "",
      //項目ソート保存
      currentSortkey: "",
      // メモ機能パラメータ
      destinationAPI: config.DMAT,
      navItemLabel: "individual.individual_diagnosis_function_active_test",
      measureId: "", // DM実行時に取得
      systemId: "",
      modalMemo: false,
      highlightItems: [],
      noSignalItems: false,
      checkLines: 0,  // グラフ項目件数
      currentTab: TAB.LIST,
      isZoomPanActive: false,
      isMemoDialogLoading: false, // memo画面でAPIと通信しているかのフラグ
      dataMonitorFunctionSettings: new dmUtil.DataMonitorFunctionSettings(dmUtil.TYPE_CALLER.AT), // DM機能の設定インスタンス
      // トレーニングモード時のメモ情報保持変数
      memoDataForTrainingMode: {
        diagnoseComment: null,
        dataName: "data1",
        memo: null
      },
      // DM開始時刻
      baseTime: null,
      // 全画面モーダルの表示有無
      showModal: false,
      // Measureコンポーネントの描画完了したか否か。Measureコンポーネントへのteleportを描画完了まで待つ必要があるため、チェックする。
      measureMounted: false,
      // SVGアイコンの種類
      ICON_KEY: ICON_KEY,
      // SVGアイコンのボタンの種類
      BUTTON_TYPE: BUTTON_TYPE,
      // 特殊機能フラグ
      isSpecial: false,
      // 押されたキーコードの配列
      keyCode: [],
      // 許可中の不変ID
      allowedImmutableId: [],
      exportPercentage: 0, // CSVエクスポートの進捗率
      isDmError: false, // DMエラー発生フラグ
    };
  },

  props: {
    // noop
  },

  components: {
    "processing-view": loading,
    "data-monitor": dataMonitor,
    "control-panel": controlPanel,
    "memo-page": memoPage,
    "cstm-table": cstmTable,
    "full-modal": fullModal,
    "no-text-svg-button": noTextSVGButton,
    "wrapped-teleport": WrappedTeleport,
    "custom-progress": customProgress,
  },

  watch: {
    // 言語設定変更時処理
    language: {
      immediate: true,
      handler() {
        if (!this.menuResponse) {
          return;
        }

        // 選択済のAT情報の多言語対応処理(タイトルと説明のみ)
        if (this.selectedAtFunc && this.selectedAtFunc[PARAM.ID]) {
          const uuid = this.selectedAtFunc[PARAM.ID].slice(0, this.selectedAtFunc[PARAM.ID].length - 2);
          const i18nAtMenuKey = `at${this.systemId}.${uuid}`;

          // menu_title_refキーが存在する場合は親子関係が存在 ※親子関係があるパターンはSP7等が該当
          const titleRefId = this.getI18nStr(`${i18nAtMenuKey}.${PARAM.TITLE_REF}`);

          let menuTitle = "";
          let menuDescription = "";

          // 親子関係の有無で翻訳の仕方を変更する
          if (!titleRefId) {
            const atI18nMenu = this.getAtI18nMenu({}, this.systemId, uuid);
            menuTitle = atI18nMenu[PARAM.TITLE];
            menuDescription = atI18nMenu[PARAM.DESCRIPT];
          } else {
            const commonKey = `at${this.systemId}.${I18N_PARENT_ID}`;
            const descriptRefId = this.getI18nStr(`${i18nAtMenuKey}.${PARAM.DESCRIPT_REF}`);
            menuTitle = this.getI18nStr(`${commonKey}.${PARAM.TITLE}_${titleRefId}`);
            menuDescription = this.getI18nStr(`${commonKey}.${PARAM.DESCRIPT}_${descriptRefId}`);
          }

          this.selectedAtFunc[PARAM.TITLE] = menuTitle;
          this.selectedAtFunc[PARAM.DESCRIPT] = menuDescription;
        }

        // 項目一覧の多言語対応処理
        const list = this.getAtI18nMenuList(this.menuResponse, this.systemId);
        this.originalFunctionList = list;
        this.formatAtFuncList();
        this.updateFilter();
        this.setSPMenuView();
        this.setColumns();
      },
    },
    showAtSelect() {
      /**
       * 戻るボタン等でAT選択画面に戻された時、アクティブテスト後処理を実行
       */
      if (this.showAtSelect) {
        this.showDmSelect = true; // DM項目選択画面表示有無初期化
        this.doAtTerminate();
      }
    },
    async showDmSelect() {
      /**
       * 戻るボタン等でDM選択画面に戻された時、アクティブテスト後処理を実行
       */
      if (this.showDmSelect) {
        // 子コンポーネントの初期化処理実行
        await this.$refs.control_panel.cancelExec(this.isAtStandby);
        this.showModal = false;
        this.measureMounted = false;
      }
    },
    isDriving: {
      immediate: true,
      handler(value) {
        if (this.inExecution || value) {
          // AT駆動中、またはDM実行中はツリーを表示しない
          this.setViewProcessMode(config.CONTAINER_EXECUTING);
        } else {
          // 両方実行中でなければ、ツリーを表示する
          this.setViewProcessMode(config.PROCESS_IDLING);
        }
      },
    },
    inExecution: {
      immediate: true,
      handler(value) {
        if (value || this.checkDriving()) {
          // AT駆動中、またはDM実行中はツリーを表示しない
          this.setViewProcessMode(config.PROCESS_REPROCONTAINER_EXECUTING_PROCESSING);
        } else {
          // 両方実行中でなければ、ツリーを表示する
          this.setViewProcessMode(config.PROCESS_IDLING);
        }
      },
    },
  },

  computed: {
    ...mapState({
      language: (state) => state.systems.language,
      netAppId: (state) => state.netAppConnector.pairingAppId,
      vehicleInfo: (state) => state.vehicle.vehicleInfo,
      isTestMode: (state) => state.netAppConnector.isTestMode,
      isPopstateNavigation: (state) => state.cache.isPopstateNavigation,
    }),
    diagSystemId() {
      return individualDiagnose.getCurrentSystemId(this);
    },
    projectId() {
      return this.vehicleInfo ? this.vehicleInfo.vehicle.project.id : null;
    },
    // 選択確定項目の不変ID
    activeTestId() {
      if (!this.selectedAtFunc) return "";
      return this.selectedAtFunc[PARAM.ID];
    },
    btnDisabled() {
      const disabeld = {
        back: this.inExecution || this.inPlaying,
        execute: this.inPlaying || this.selectedFuncs().length === 0,
        memo: this.inExecution || !this.measureId,
        graph: !this.checkLines || this.isZoomPanActive,
        resetZoom: isResetZoomDisabled({
          inExecution: this.inExecution,
          inPlaying: this.inPlaying,
          hasCheckLines: Boolean(this.checkLines),
          currentTab: this.currentTab,
        }),
        export: this.inExecution || !this.measureId
      };
      return disabeld;
    },
  },

  // 遷移してきた画面のパラメータ情報を保持
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      // 遷移してきたときのコンポーネント内ガード共通処理
      vm.beforeRouteProcess(vm, from);
    });
  },

  /**
   * 遷移してきた画面のパラメータ情報を保持
   * 同一コンポーネントが再帰的に利用された場合、呼び出される
   */
  beforeRouteUpdate(to, from, next) {
    if (this.isPopstateNavigation) {
      this.cacheIsPopstateNavigation(false);
      next();
      return;
    }
    this.$nextTick(() => {
      const token = session.getToken();
      if (token) {
        // 計測中は移動不可
        if (this.inExecution || this.checkDriving()) {
          // セッションが続いているのでアラート表示して続行
          this.inExecutionAlert();
          return;
        }
      }
      // アクティブテスト後処理
      this.doAtTerminate();
      // 遷移してきたときのコンポーネント内ガード共通処理
      this.beforeRouteProcess(this, from);
      // mounted時に行う共通処理を呼び出す
      this.mountedLogic();
    });
    next();
  },

  beforeRouteLeave(to, from, next) {
    if ((to.name === "login") && (to.fullPath === "/") && (to.path === "/")) {
      this.$refs.control_panel.setSignout();
      if (this.$refs.data_monitor
        && this.$refs.data_monitor.setSignout
        && typeof this.$refs.data_monitor.setSignout === "function"
      ) {
        this.$refs.data_monitor.setSignout();
      }
      next();
      return;
    }
    if (to.name === "changeUnit") {
      const token = session.getToken();
      if (token) {
        if (!this.showAtSelect) {
          // 単位設定は移動不可
          this.changeUnitAlert();
          return;
        }
      }
    }
    this.$nextTick(async () => {
      const token = session.getToken();
      if (token) {
        // 計測中は移動不可
        if (this.inExecution || this.checkDriving()) {
          // セッションが続いているのでアラート表示して続行
          this.inExecutionAlert();
          // next(false);
          return;
        }
      }
      this.processing = true;
      await this.$refs.control_panel.cancelExec(this.isAtStandby);
      // アクティブテスト後処理
      await this.doAtTerminate();
      this.toggleShowDmSelect(true);
      this.processing = false;
      next();
    });
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),
    ...mapActions("observation", ["usageCountUp"]),
    ...mapActions("app", ["setViewProcessMode"]),
    ...mapActions('cache', ['cacheIsPopstateNavigation']),
    ...createZoomTabHandlersForOptions({
      isResetZoomDisabled() {
        return this.btnDisabled.resetZoom;
      },
    }),

    /**
     * componentのmounted時に呼び出す処理
     */
    async mountedLogic() {
      this.systemId = this.diagSystemId;
      this.setColumns();
      await this.updateTable();
      this.formatAtFuncList();
    },

    /**
     * 選択状態を解除し、フィルターの文字列に応じてリストをフィルターする
     */
    updateFilter() {
      this.currentSortkey = "";
      if (this.showAtSelect || !this.selectedActiveTest) {
        // アクティブテスト選択画面の場合は、選択中のアクティブテストをクリアする
        this.selectedActiveTest = {};
      } else {
        this.selectedActiveTest = this.originalFunctionList.find(
          (func) => func[PARAM.ID] === this.selectedActiveTest[PARAM.ID]
        ) || {};
      }

      this.setHighLight();

      if (!this.originalFunctionList || !this.originalFunctionList.length) {
        this.filteredFunctionList = [];
      } else {
        this.filteredFunctionList = this.originalFunctionList.filter(
          (item) =>
            item[PARAM.TITLE] &&
            item[PARAM.TITLE].toLowerCase() &&
            item[PARAM.TITLE]
              .toLowerCase()
              .includes(this.filterText.toLowerCase()) &&
            !this.checkExclusionSpKey(item[PARAM.ID])
        );
      }
    },

    /**
     * 選択した機能を変数に保持
     */
    clickRow(selected) {
      // cstm-tableのソート順を保存
      const sortDir =
        this.$refs.filtered_tbl.currentSortDir === config.ASC
          ? config.DESC
          : config.ASC;
      const sort_now = this.$refs.filtered_tbl.currentSort;

      // 項目選択
      this.selectedActiveTest = this.originalFunctionList.find(
        (func) => func[PARAM.ID] === selected[PARAM.ID]
      );
      this.setHighLight();
      this.isSpecial = Object.values(SPAT_KEY_LIST).flat().includes(this.selectedActiveTest[PARAM.ID]);

      // cstm-tableのソートを再実行
      this.currentSortkey = sort_now;
      this.$refs.filtered_tbl.currentSort = sort_now;
      this.$refs.filtered_tbl.currentSortDir = sortDir;
      this.$refs.filtered_tbl.sort({ name: sort_now });
      this.$refs.filtered_tbl.getSortIcon();
    },

    /**
     * 選択している機能のハイライト設定
     */
    setHighLight() {
      this.filteredFunctionList.forEach((item) => {
        if (item[PARAM.ID] === this.selectedActiveTest[PARAM.ID]) {
          item[PARAM.FIELD] = {
            menu_title: config.LIST_ITEM_HIGHLIGHT,
          };
        } else if (item[PARAM.FIELD]) {
          delete item[PARAM.FIELD];
        }
      });
    },

    /** アクティブテスト前処理 */
    async doAtStandby() {
      // await doStandby(this, false);
      const token = session.getToken();
      const reqBody = atUtil.genReqBodyStandby(token.id_token, false);
      await this.doCommonAtApi(
        reqBody,
        (response) => {
          let checkResponse = true;
          if (response.data.user_data && Array.isArray(response.data.user_data)) {
            for (const userData of response.data.user_data) {
              if (userData.res_type !== RES_TYPE.OK) {
                checkResponse = false;
                break;
              }
            }
          }
          if (!checkResponse) {
            // 共通エラーを表示
            atUtil.showUnexpectedError(
              this,
              ERR_KEY.COMMON,
              this.requirementId,
              null, // 共通エラーはnull
              async () => {
                await atUtil.doTerminate(this, true);
                this.backSelect();
              }
            );
          }
          else {
            this.isAtStandby = true;
          }
        },
        () => {
          // 共通エラーを表示
          atUtil.showUnexpectedError(
            this,
            ERR_KEY.COMMON,
            this.requirementId,
            null, // 共通エラーはnull
            async () => {
              await atUtil.doTerminate(this, true);
              this.backSelect();
            }
          );
        }
      );
    },

    /** アクティブテスト後処理 */
    async doAtTerminate() {
      // アクティブテスト前処理実施前 or アクティブテスト後処理実施済の場合は何もしない
      if (!this.isAtStandby) return;

      const token = session.getToken();
      if (token) {
        const requestBody = atUtil.genReqBodyTerminate(
          token.id_token,
          false,
          this.selectedAtFunc
        );
        await this.doCommonAtApi(
          requestBody,
          (response) => {
            let checkResponse = true;
            if (response.data.user_data && Array.isArray(response.data.user_data)) {
              for (const userData of response.data.user_data) {
                if (userData.res_type !== RES_TYPE.OK) {
                  checkResponse = false;
                  break;
                }
              }
            }
            if (!checkResponse) {
              // 共通エラーを表示
              atUtil.showUnexpectedError(
                this,
                ERR_KEY.COMMON,
                this.requirementId,
                null, // 共通エラーはnull
                async () => {
                  await atUtil.doTerminate(this, true);
                  this.backSelect();
                }
              );
            }
          },
          () => {
            // 共通エラーを表示
            atUtil.showUnexpectedError(
              this,
              ERR_KEY.COMMON,
              this.requirementId,
              null, // 共通エラーはnull
              async () => {
                await atUtil.doTerminate(this, true);
                this.backSelect();
              }
            );
          }
        );
        this.isAtStandby = false;
      }
    },

    /** アクティブテスト選択 */
    async selectAtFunc() {
      // 未選択時はreturn
      if (!this.selectedActiveTest || !this.selectedActiveTest[PARAM.ID]) {
        return;
      }

      // イベントログの操作 ID 発行
      this.processing = true;
      await eventLogUtil.createEventLogHandleId(this.netAppId);
      // アクティブテスト各機能の利用回数カウントアップ
      this.usageCountUp({
        type: config.USAGE_COUNT_AT,
        systemName: this.systemId,
        immutableId: this.selectedActiveTest[PARAM.ID],
      });

      this.selectedAtFunc = this.selectedActiveTest;
      const tmpHighLightItems = this.selectedActiveTest.highlight ?
        this.selectedActiveTest.highlight.map(item => dmUtil.setSystem(this.systemId, item)) : [];
      // HEV系AT対応：IO/Routine Control対象は強調表示
      if (this.selectedActiveTest.at_info) {
        for (const info of this.selectedActiveTest.at_info) {
          tmpHighLightItems.push(dmUtil.setSystem(this.systemId, info.id));
        }
      }
      this.highlightItems = tmpHighLightItems;

      // i18nMenuTitleKeyの設定
      this.i18nMenuTitleKey = atUtil.getAtFunctionTranslateKey(this, this.selectedAtFunc[PARAM.ID], this.systemId);
      // アクティブテスト前処理
      await this.doAtStandby();
      this.processing = false;
      if (!this.isAtStandby) {
        // doAtStandbyで失敗した場合return
        return;
      }

      // 子コンポーネントの初期化処理実行
      await this.$refs.control_panel.btnAtSelectOnClicked();

      // 画面切り替え
      this.toggleShowAtSelect(false);
    },

    /** データモニタ項目選択->アクティブテスト項目選択 */
    backSelect() {
      this.toggleShowAtSelect(true);
    },
    /** データモニタ計測->データモニタ項目選択 */
    backMeasure() {
      this.$refs.control_panel.clearAtMsg();
      // 計測中、駆動中は移動不可
      if (this.inExecution || this.checkDriving()) {
        this.inExecutionAlert();
      } else if (!this.selectedActiveTest.data_monitor) {
        this.toggleShowAtSelect(true);
      } else {
        // 初期化
        this.checkLines = 0;
        this.toggleShowDmSelect(true);
      }
    },

    /** 表示切替（アクティブテストの項目選択/データモニタ画面） */
    toggleShowAtSelect(flg) {
      if (flg === undefined || flg === null) {
        this.showAtSelect = !this.showAtSelect;
      } else {
        this.showAtSelect = flg;
      }

      /**
       * AT項目選択画面を表示時はDMも選択項目画面表示フラグにする。
       * data_monitorがfalseのとき、信号選択画面をスキップする。
       */
      if (!this.selectedActiveTest.data_monitor) {
        this.toggleShowDmSelect(false);
      }
    },

    /** 表示切替（データモニタ項目選択/データモニタ計測） */
    toggleShowDmSelect(flg) {
      // ATの項目選択画面を表示時は無効
      if (this.showAtSelect) return;
      if (flg === undefined || flg === null) {
        this.showDmSelect = !this.showDmSelect;
      } else {
        this.showDmSelect = flg;
      }
    },
    /** 状態切替（駆動開始/停止状態） */
    toggleDrive(flg) {
      if (flg === undefined || flg === null) {
        this.isDriving = !this.isDriving;
      } else {
        this.isDriving = flg;
      }
    },
    /** 状態切替（計測開始/停止状態） */
    toggleExecute(flg) {
      if (flg === undefined || flg === null) {
        this.inExecution = !this.inExecution;
      } else {
        this.inExecution = flg;
      }
    },
    /** 状態切替（計測データ再生/停止状態） */
    togglePlaying(flg) {
      if (flg === undefined || flg === null) {
        this.inPlaying = !this.inPlaying;
      } else {
        this.inPlaying = flg;
      }
      if (this.inPlaying) {
        this.isZoomPanActive = false;
      }
    },
    /** 状態切替（データ読み出し実行フラグ） */
    toggleReadExecuted(flg) {
      this.isReadExecuted = flg;
    },

    /** baseTime変更検知 */
    changeBaseTime(val) {
      this.baseTime = val;
    },

    /** モニタ開始 */
    startMonitor() {
      this.$refs.data_monitor.startMonitor();
    },

    /** 
     * モニタ停止 
     * @param {Function} callback - モニタ停止完了後に呼び出されるコールバック関数  (SP7専用)
     */
    async stopMonitor(callback) {
      try {
        // SP2の場合データモニタの停止前にアクティブテスト停止と終了をする。
        if (this.isDriving && this.selectedAtFunc && this.selectedAtFunc[PARAM.ID_REQUIREMENT] === "REQ.ACT.SP2") {
          this.processing = true;
          await this.$refs.control_panel.stopDrive();
          this.processing = false;
          if (this.inExecution) {
            this.$refs.data_monitor.stopMonitor(this.selectedActiveTest[PARAM.ID]);
          }
        } else if (this.selectedAtFunc[PARAM.ID_REQUIREMENT] === "REQ.ACT.SP7" && callback && typeof callback === "function") {
          // SP7でアクティブテストでSTATUSエラーが発生した場合は、アクティブテスト停止処理前にモニタ停止を完了されるために同期的にモニタ停止を行う。
          await this.$refs.data_monitor.stopMonitor(this.selectedActiveTest[PARAM.ID]);
        } else {
          this.$refs.data_monitor.stopMonitor(this.selectedActiveTest[PARAM.ID]);
        }
      } finally {
        if (callback && typeof callback === "function") {
          // SP7:callbackで受け取ったresolveを実行して、stopMonitorの処理が完了したことを呼び出し元に通知する
          callback();
        }
      }

    },

    /** HEV系AT: IO/Routine Control項目を空欄に設定する(DM計測中のみ) */
    async dmChangeToBlank() {
      if (!this.inExecution) return;
      const target = [];
      const currentTime = Date.now();
      for (const item of this.$refs.data_monitor.atInfo) {
        target.push([{
          data_monitor_id: dmUtil.setSystem(this.systemId, item.id),
          convertedData: "",
          time: currentTime
        }]);
      }
      // 最後の計測値が降りてきてから処理するため、1000msウェイト(最大1000ms毎にデータ降りて来るため)
      await util.sleep(1000);
      this.$refs.data_monitor.dmValueChange(target);
    },

    /** 計測IDを設定 */
    setMeasuredId(id) {
      this.measureId = String(id);
    },
    /** メモを開く */
    openMemo() {
      this.modalMemo = true;

      // トレーニングモード用のメモ情報設定
      if (this.isTestMode) {
        this.$nextTick(() => {
          this.$refs.memo.diagnoseComment = this.memoDataForTrainingMode.diagnoseComment;
          this.$refs.memo.dataName = this.memoDataForTrainingMode.dataName;
          this.$refs.memo.memo = this.memoDataForTrainingMode.memo;
        });
      }
    },

    /** グラフ項目件数を変更 */
    changeCheckValue(checkLines) {
      this.checkLines = checkLines;
    },

    /**
     * ズーム/パン状態を更新
     * @param {boolean} isActive
     */
    zoomPanStateChanged(isActive) {
      this.isZoomPanActive = isActive;
    },

    /** メモ情報の保存 */
    async memoSave() {
      // トレーニングモード用のメモ情報設定
      if (this.isTestMode) {
        this.processing = true;
        this.memoDataForTrainingMode.diagnoseComment = this.$refs.memo.diagnoseComment;
        this.memoDataForTrainingMode.dataName = this.$refs.memo.dataName;
        this.memoDataForTrainingMode.memo = this.$refs.memo.memo;
        await util.sleep(1000);
        this.processing = false;
      }

      await this.$refs.memo.save();
    },

    /** メモ情報のクリア */
    memoClear: function () {
      this.$refs.memo.clear();
    },

    /** メモを閉じる */
    closeNoteModal() {
      this.modalMemo = false;
    },

    /** グラフ設定 */
    handleConfigureGraph() {
      this.$refs.data_monitor.handleConfigureGraph();
    },

    /**
     * 多言語対応キーで取得値か空文字を返す
     * @param {*} key
     * @returns
     */
    getI18nStr(key) {
      return this.$te(key) ? this.$t(key) : "";
    },

    /**
     * アクティブテストの多言語変換の一覧取得
     * @param {Array} menuResponse メニュー一覧
     * @param {string} systemId システム ID
     */
    getAtI18nMenuList(menuResponse, systemId) {
      const res = [];
      menuResponse.sort((a, b) => {
        if (a[PARAM.SORT] !== b[PARAM.SORT]) {
          // ソートIDでソート
          return a[PARAM.SORT] - b[PARAM.SORT];
        } else {
          // didでソート（ソートIDが一致時）
          if (
            a[PARAM.CTRL_STATE][PARAM.DID].length &&
            b[PARAM.CTRL_STATE][PARAM.DID].length
          ) {
            // 最初のdid同士で比較
            return a[PARAM.CTRL_STATE][PARAM.DID][0] >
              b[PARAM.CTRL_STATE][PARAM.DID][0]
              ? 1
              : -1;
          } else if (a[PARAM.CTRL_STATE][PARAM.DID].length) {
            return 1;
          } else if (b[PARAM.CTRL_STATE][PARAM.DID].length) {
            return -1;
          }
          return 0;
        }
      });

      const childMap = {};
      // 多言語変換リストの取得
      menuResponse.forEach((item) => {
        const uuid = item[PARAM.ID].slice(0, item[PARAM.ID].length - 2);
        const i18nAtMenuKey = `at${systemId}.${uuid}`;

        // menu_title_refキーが存在する場合は親子関係が存在 ※親子関係があるパターンはSP7等が該当
        const titleRefId = this.getI18nStr(
          `${i18nAtMenuKey}.${PARAM.TITLE_REF}`
        );

        if (!titleRefId) {
          // 親子関係がなければそのまま
          res.push(this.getAtI18nMenu(item, systemId, uuid));
        } else {
          // 親データ生成、子データ一時退避
          if (!childMap[titleRefId]) {
            // 初回子データ処理
            // 子データ一時保管場所を用意
            childMap[titleRefId] = [];
            const commonKey = `at${systemId}.${I18N_PARENT_ID}`;

            // タイトルと説明を取得
            const descriptRefId = this.getI18nStr(
              `${i18nAtMenuKey}.${PARAM.DESCRIPT_REF}`
            );
            const parentTitle = this.getI18nStr(
              `${commonKey}.${PARAM.TITLE}_${titleRefId}`
            );
            const parentDescript = this.getI18nStr(
              `${commonKey}.${PARAM.DESCRIPT}_${descriptRefId}`
            );

            // 親データを生成・追加
            const parent = {
              active_test_id: item[PARAM.ID],
              requirement_id: item[PARAM.ID_REQUIREMENT],
              menu_title: parentTitle,
              menu_description: parentDescript,
              sort_id: item.sort_id,
              title_id: titleRefId,
              values: [],
              data_monitor: item[PARAM.DATA_MONITOR],
            };

            res.push(parent);
          }
          const itemName = this.getI18nStr(
            `${i18nAtMenuKey}.${PARAM.TITLE_ITEM}`
          );
          const child = {
            item_name: itemName,
            ...item,
          };
          childMap[titleRefId].push(this.getAtI18nMenu(child, systemId, uuid));
        }
      });
      // 退避した子データを反映
      res.forEach((item) => {
        if (item[PARAM.TITLE_ID]) {
          item[PARAM.VALS].push(childMap[item[PARAM.TITLE_ID]]);
        }
      });

      return res.filter(
        // 項目名称の翻訳ファイルが存在しないデータは除外
        (item) => item.menu_title
      );
    },

    /**
     * アクティブテストの多言語変換の一覧取得
     * @param {Array} menuItem メニュー
     * @param {string} systemId システム ID
     * @param {string} uuid 不変IDの下２桁を除去
     */
    getAtI18nMenu(menuItem, systemId, uuid) {
      /**
       * 翻訳キーとメッセージキーを元に文言が存在すれば、その翻訳後の文言を返す。
       * ない場合は、defaultMessage を返す（defaultMessage 未指定の場合は空文字を返す）
       * @param {string} i18nDmMenuKey 翻訳キー
       * @param {string} messageKey メッセージキー
       * @returns {string | undefined} 翻訳後の文言（見つからなかった場合は undefined）
       */
      const convertI18nText = (i18nAtMenuKey, messageKey) => {
        const key = `${i18nAtMenuKey}.${messageKey}`;
        return this.getI18nStr(key);
      };
      const i18nAtMenuKey = `at${systemId}.${uuid}`;

      return {
        menu_title: convertI18nText(i18nAtMenuKey, [PARAM.TITLE]),
        menu_description: convertI18nText(i18nAtMenuKey, [PARAM.DESCRIPT]),
        ...menuItem,
      };
    },

    /** カラム設定 */
    setColumns() {
      const columns = [
        {
          name: "menu_title",
          label: this.$t("individual.header_active_test_table_item"),
          useHtmlBind: true,
        },
      ];
      this.columns = columns;
    },

    /**
     * API取得結果をAT項目一覧に整形する
     */
    formatAtFuncList() {
      if (!this.originalFunctionList || !this.originalFunctionList.length)
        return;
      const list = [];
      const groupParamList = [];

      for (const func of this.originalFunctionList) {
        // 特殊メニューの表示判定
        if (this.checkExclusionSpKey(func[PARAM.ID])) {
          continue;
        }

        if (!func[PARAM.TITLE_REF]) {
          list.push(func);
        } else {
          // グループ項目対応処理
          if (!groupParamList.includes(func[PARAM.TITLE_REF])) {
            // 初回のグループ項目IDの場合、親データとしてリストに追加
            const tmpItem = {
              menu_title_ref: func[PARAM.TITLE_REF],
              values: [],
              ...func,
            };
            list.push(tmpItem);
            groupParamList.push(func[PARAM.TITLE_REF]);
          } else {
            // 2回目以降のグループ項目IDの場合、子データとしてリストに追加
            const idx = list.findIndex(
              (e) => e[PARAM.TITLE_REF] === func[PARAM.TITLE_REF]
            );
            list[idx].values.push(func);
          }
        }
      }

      this.filteredFunctionList = list;
    },

    /**
     * 呼び出し時のコンポーネント内ガードの共通処理
     * @param vm 呼び出し元のオブジェクト(this)
     * @param from 遷移元
     */
    beforeRouteProcess(vm, from) {
      if (util.isFromSettingsPage(from.name) || util.isSameSystemPage(from)) {
        // 単位設定画面またはパスワード変更画面からの遷移時、または同じシステム内の子機能間への遷移の場合 trueを指定
        vm.isUseCache = true;
      } else {
        // 上記以外の場合、falseを指定
        vm.isUseCache = false;
      }
    },

    /** 計測中警告ダイアログ */
    inExecutionAlert() {
      // AT駆動中エラーを優先表示
      const label = this.checkDriving()
        ? "individual.alert_active_test_measurement"
        : "individual.alert_data_monitor_measurement";
      alert.showWarning(this, null, this.$t(label), () => { });
    },

    /** 単位設定警告ダイアログ */
    changeUnitAlert() {
      const label = "individual.alert_data_monitor_change_unitsetting";
      alert.showWarning(this, null, this.$t(label), () => { });
    },

    /** エラー時のAPI後処理 */
    errTerminate() {
      const token = session.getToken();
      if (!token) return;
      const requestBody = atUtil.genReqBodyTerminate(token.id_token, true);
      this.doCommonAtApi(requestBody);
    },

    /** AT失敗時のDM停止・画面遷移 */
    async atFailure() {
      const self = this;
      if (this.inExecution) {
        await this.$refs.data_monitor.stopMonitor(this.selectedActiveTest[PARAM.ID]);
      }
      this.toggleDrive(false);
      this.toggleExecute(false);
      this.$nextTick(() => {
        self.backMeasure();
        // 子コンポーネントのunmountが呼ばれるように、dmSelectがtrueの状態で1度だけ描画する
        this.$nextTick(() => {
          self.backSelect();
        });
      });
    },

    /**
     * データモニタでエラーが発生した場合の処理
     * @param {string} message ダイアログに表示するメッセージ。未指定の場合は共通エラーとして表示
     * @param {boolean} isWarning ダイアログの種類が警告か否か
     */
    async dmErrorHandling(message = '', isWarning = false) {
      try {
        this.processing = true;
        this.isDmError = true;

        // アクティブテスト実行中にエラーが発生した場合のマーカ登録
        const markInfo = {
          "type": AT_EXE_TYPE.ERROR
        };
        await this.saveActiveTestExecuteMark(markInfo);

        // アクティブテスト停止と終了。ただしSP7はterminate不要。
        await this.$refs.control_panel.cancelExec(this.isAtStandby);
        if (this.isDriving && this.selectedAtFunc && this.selectedAtFunc[PARAM.ID_REQUIREMENT] !== "REQ.ACT.SP7") {
          await atUtil.doTerminate(this, true);
        }
      } finally {
        this.isDmError = false;
        this.processing = false;
      }

      const callbackFunction = async () => {
        // データモニタ停止や画面遷移中に再度ローディングを表示する
        this.processing = true;
        await this.atFailure();
        this.processing = false;
      };

      if (message) {
        // ダイアログのメッセージ指定時はそちらを使用
        if (isWarning) {
          alert.showWarning(this, null, message, callbackFunction);
        } else {
          alert.showError(this, null, message, callbackFunction);
        }
      } else {
        // それ以外は共通エラーとして表示
        atUtil.showUnexpectedError(
          this,
          ERR_KEY.COMMON,
          this.requirementId,
          null, // 共通エラーはnull
          callbackFunction
        );
      }
    },

    /******************** 以下API処理 ********************/

    /** テーブル情報の更新  */
    async updateTable() {
      try {
        this.processing = true;
        // イベントログの操作 ID 発行
        await eventLogUtil.createEventLogHandleId(this.netAppId);
        // データモニタおよびアクティブテストの初期化通信のポーリング処理を行う
        const response = await individualDiagnose.pollingApi(
          this,
          // POST API 呼び出しのコールバック
          async () => {
            return await api.postCall(
              config.DMAT,
              "/netApps/" +
              this.netAppId +
              "/initialize",
              {
                use_cache: this.isUseCache,
                is_active_test: true,
                system_name: [this.systemId] // アクティブテストは個別診断のみのため、1つのみ指定
              }
            );
          },
          // GET API 呼び出しのコールバック
          async (postApiResponse) => {
            return await api.getCall(
              config.DMAT,
              "/netApps/" +
              this.netAppId +
              "/initialize/requests/" +
              postApiResponse.data.request_id
            );
          },
          // エラー発生のコールバック
          (errorType) => {
            errorType;
            atUtil.showUnexpectedError(this, ERR_KEY.INIT);
          },
          // POST API レスポンスチェックのコールバック
          (postApiResponse) => {
            // リクエストID が正常に返ってきたら成功とする
            return postApiResponse &&
              postApiResponse.data &&
              postApiResponse.data.request_id
              ? ""
              : config.INDIVIDUAL_API_CALL_ERROR_POST_INVALID_RESPONSE;
          },
          // GET API レスポンスチェックのコールバック
          (getApiResponse) => {
            let result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
            if (
              getApiResponse &&
              getApiResponse.data &&
              getApiResponse.data.status
            ) {
              switch (getApiResponse.data.status) {
                case config.STATUS_DMAT_COMPLETED:
                  // ポーリング終了を返す
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                case config.STATUS_DMAT_IN_PROGRESS:
                  // ポーリング続行を返す
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  break;
                case config.STATUS_DMAT_FAILED:
                default:
                  // それ以外はエラー発生を返す
                  break;
              }
            }
            return result;
          },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => {
            atUtil.validateErrorResult(this, result);
          },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => {
            atUtil.validateErrorResult(this, result);
          }
        );

        if (response) {
          // ポーリングが正常に完了した場合は、アクティブテスト一覧データ取得処理を行う
          this.menuResponse = await this.getMenuResponse(
            this.netAppId,
            this.systemId
          );
          // responseが存在しない場合、処理終了
          if (!this.menuResponse) {
            return;
          }
          // ソートIDの入れ替え※特殊機能のソートIDを先頭に変更する処理
          this.sortIdChange();
          // アクティブテスト一覧の多言語変換リストの取得
          const menuList = this.getAtI18nMenuList(
            this.menuResponse,
            this.systemId
          );
          this.originalFunctionList = menuList;
          this.updateFilter();

          // 表示項目がない場合、非サポート画面を表示
          if (this.filteredFunctionList.length < 1) {
            // OK押下時は何もしない
            alert.showConfirmation(this, null, this.$t('individual.label_not_supported_message'), null, null, null, true);
          }
        }
      } finally {
        this.processing = false;
      }
    },

    /**
     * 一覧取得処理を行う関数
     * @param {string} netAppId クラウドコネクタ ID
     * @param {string} systemId システム ID
     * @returns 成功時は menus リストを返す（失敗時は null）
     */
    async getMenuResponse(netAppId, systemId) {
      const response = await individualDiagnose.syncApi(
        this,
        // GET API 呼び出しのコールバック
        async () => {
          return await api.getCall(
            config.DMAT,
            "/netApps/" + netAppId + "/systems/" + systemId + "/menus"
          );
        },
        // エラー発生のコールバック
        (errorType) => {
          errorType;
          atUtil.showUnexpectedError(this);
        },
        // GET API レスポンスチェックのコールバック
        (getApiResponse) => {
          // menus が入っていれば成功とする
          return getApiResponse &&
            getApiResponse.data &&
            getApiResponse.data.menus
            ? ""
            : config.INDIVIDUAL_API_CALL_ERROR_GET_INVALID_RESPONSE;
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        }
      );
      // 処理失敗時は response が null になるので考慮
      return response ? response.data.menus : null;
    },

    /**
     * API汎用処理（standby, terminate, errorTerminate）
     * standby: NonDefaultSession遷移・セキュリティアクセス処理
     * terminate: DefaultSession遷移
     * errorTerminate: DefaultSession遷移
     * @param {*} requestBody
     * @returns
     */
    async doCommonAtApi(
      requestBody,
      completedCallback = undefined,
      failedCallback = undefined
    ) {
      try {
        // API実行
        const response = await atUtil.pollingAtApi(
          this,
          this.netAppId,
          this.systemId,
          this.activeTestId,
          requestBody,
          // エラー発生のコールバック
          (errorType) => {
            errorType;
            atUtil.showUnexpectedError(this);
          },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => {
            atUtil.validateErrorResult(this, result);
          },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => {
            atUtil.validateErrorResult(this, result);
          }
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_DMAT_COMPLETED:
              if (typeof completedCallback === 'function') completedCallback(response);
              break;
            case config.STATUS_DMAT_FAILED:
              if (typeof failedCallback === 'function') {
                failedCallback();
              } else {
                atUtil.showUnexpectedError(this);
              }
              break;
            default:
              atUtil.showUnexpectedError(this);
              break;
          }
        }
      } catch (e) {
        atUtil.showUnexpectedError(this);
      }
    },

    /**
     * 駆動中かどうかをチェックする
     * @returns {boolean} 駆動中かどうか
     */
    checkDriving() {
      if ((!this.$refs) || (!this.$refs.control_panel) || (!this.$refs.control_panel.checkDriving) ||
        ((typeof this.$refs.control_panel.checkDriving) !== "function")) {
        return this.isDriving;
      }
      return this.$refs.control_panel.checkDriving();
    },

    /**
     * データモニタで選択されている機能リストを取得する
     * @returns {Array} 選択されている機能リスト
     */
    selectedFuncs() {
      return this.$refs.data_monitor &&
        this.$refs.data_monitor.selectedFuncs || [];
    },

    /**
     * データモニタで選択されている機能リストをHTMLエスケープ処理する
     * @param {Array} items 機能リスト
     * @returns {Array} HTMLエスケープ処理を実行した機能リスト
     */
    escapeFunctionList(items) {
      return items.map(i => ({
        ...i,
        [PARAM.TITLE]: util.escapeHTML(i[PARAM.TITLE])
      })
      );
    },

    /**
     * 信号を選択していない場合、メモ、グラフ設定ボタンを消す
     * @param {boolean} value 信号が選択されていないかどうか
     */
    changeNoSignal(value) {
      this.noSignalItems = value;
    },

    /**
     * 最大化ボタン押下処理
     */
    maximum() {
      this.showModal = true;
    },

    /**
     * 最小化ボタン押下処理
     */
    minimum() {
      this.showModal = false;
    },

    /**
     * 最小化ボタン押下処理
     */
    modalContentId() {
      return `full-modal-content-${this.dataMonitorFunctionSettings.typeCaller}`;
    },

    /**
     * 計測画面全体をteleportさせるモーダルダイアログのIDを作成。再生画面でのID被りを避けるため、画面ごとの値を設定
     * @returns {string} 作成したID
     */
    graphSettingsDestinationId() {
      return `graph-settings-destination-${this.dataMonitorFunctionSettings.typeCaller}`;
    },

    /**
     * 子コンポーネントからDataMonitorMeasureのMount・Unmountの通知を受け取る
     * @param {boolean} isMounted Mountされたか否か
     */
    measureMountedEvent(isMounted) {
      this.measureMounted = isMounted;
      if (!isMounted) {
        // 計測画面のコンポーネントが破棄されたら、計測画面を離れたと判断して全画面モーダルも閉じる
        this.showModal = false;
      }
    },

    /**
     * 非表示にする特殊機能かをチェックする
     * @param {string} immuTableId 不変ID
     * @returns {boolean} true：非表示にする特殊機能に該当する
     */
    checkExclusionSpKey(immuTableId) {
      // 特殊機能の不変IDに該当する、且つ許可中の不変IDに該当しない
      return Object.values(SPAT_KEY_LIST).flat().includes(immuTableId) && !this.allowedImmutableId.includes(immuTableId);
    },

    /**
     * キーが離されたら呼ばれる
     * @param {Object} event イベント
     */
    onKeyUp(event) {
      const i = this.keyCode.indexOf(event.keyCode);
      // 配列にキーコードがあればその要素を削除
      if (i !== -1) {
        this.keyCode.splice(i, 1);
      }
      // キーが3つの場合
      if (this.keyCode.length === 3) {
        this.inputKey();
      }
    },

    /**
     * キーが押されたら呼ばれる
     * @param {Object} event イベント
     */
    onKeyDown(event) {
      // 配列に値がない、または処理中でなければ
      if (-1 === this.keyCode.indexOf(event.keyCode) && !this.processing) {
        // キーコードを追加
        this.keyCode.push(event.keyCode);
        // キーが3つの場合
        if (this.keyCode.length === 3) {
          this.inputKey();
        }
      }
    },

    /** 特殊キーチェック */
    inputKey() {
      const keyPushed = SPAT_KEY_CODE.find(obj => JSON.stringify(obj.code) === JSON.stringify(this.keyCode.sort((a, b) => { return a - b; })));
      const filteredKeyList = this.filteredFunctionList.map(func => func[PARAM.ID]);
      if (typeof keyPushed !== 'undefined') {
        keyPushed.key.forEach(key => {
          if (!filteredKeyList.includes(key)) {
            this.allowedImmutableId.push(key); // 表示対象の不変ID
            this.formatAtFuncList();
            this.setSPMenuView();
          }
        });
      }
    },

    /**
     * アクティブテスト用のマーカ情報の保存
     * @param {object} markInfo マーカー登録情報
     * @returns {boolean} 処理の成否
     */
    async saveActiveTestExecuteMark(markInfo) {
      //マーカー登録情報にtypeが存在しない場合はマーカー登録を実施しない
      if (markInfo.type === undefined) return false;

      const menu_title = this.selectedAtFunc[PARAM.TITLE];
      let comment = "";

      switch (markInfo.type) {
        case AT_EXE_TYPE.START: //アクティブテスト駆動時
          comment = this.$t('individual.active_test_mark_message_start', [menu_title]);
          if (markInfo.info) {
            markInfo.info.forEach((info) => {
              // 共通ボタンパネルからの遷移時は、ボタンのラベル部分のみを登録する
              comment += `\n${info.param ? `${info.param}:` : ''}${info.value}`;
            });
          }
          break;
        case AT_EXE_TYPE.END: //アクティブテスト停止時
          comment = this.$t('individual.active_test_mark_message_end', [menu_title]);
          break;
        case AT_EXE_TYPE.ERROR: //アクティブテスト実行中のエラー発生時
          comment = this.$t('individual.active_test_mark_message_error', [menu_title]);
          break;
        default:
          //想定外の値が入っていた場合はマーカー登録を実施しない
          return false;
      }

      return await this.$refs.data_monitor.externalSaveMarker(comment);
    },

    /**
     * 特殊機能メニューの表示変更設定 ※先頭に[*]付与
     */
    setSPMenuView() {
      this.filteredFunctionList.forEach((item) => {
        if ((SPAT_KEY_LIST.CTRL_ALT_I.includes(item.active_test_id)) && (!item.isSpMenuViewChange)) {
          item.menu_title = "*" + item.menu_title;
          item.isSpMenuViewChange = true;
        }
      });
    },

    /**
     * 特殊機能を表示した際に先頭に表示されるようにソートIDを変更
     */
    sortIdChange() {
      // 特殊機能の不変IDを抽出
      let spFuncCnt = 0;
      this.menuResponse.forEach(item => {
        if (SPAT_KEY_LIST.CTRL_ALT_I.includes(item.active_test_id)) {
          spFuncCnt += 1;
        }
      });
      // 特殊機能がない場合は変更不要
      if (spFuncCnt === 0) return;
      // sort_id変更
      let num = 1;
      this.menuResponse.forEach(item => {
        if (SPAT_KEY_LIST.CTRL_ALT_I.includes(item.active_test_id)) {
          // 特殊機能の場合は1からsort_idを振りなおす
          item.sort_id = num;
          num += 1;
        } else {
          // 通常機能の場合は特殊機能の数分sort_idをずらす
          item.sort_id += spFuncCnt;
        }
      });
    },

    /**
     * CSVエクスポートの進捗更新
     * @param {number} allCount 全体数
     * @param {number} currentCount 現在の数
     */
    updateExportProgress(allCount, currentCount) {
      this.exportPercentage = dmUtil.calculatePercentage(allCount, currentCount);
    },

    /**
     * ファイルエクスポートボタン押下時の処理
     */
    async csvExport() {
      let csvWriter = null;
      try {
        this.showCsvProgress = true;
        this.exportPercentage = 0;
        let measurementId = this.$refs.data_monitor.getMeasurementId();

        // project情報を取得
        const endPoint = '/projects/' + this.vehicleInfo.vehicle.project.id + '/measures';
        const { dataName, projectDetails } = await dmUtil.fetchProjectDetails(this.destinationAPI, endPoint, this.vehicleInfo, measurementId, this.isTestMode, this.memoDataForTrainingMode);
        if (this.isTestMode) {
          // トレーニングモードの場合は固定のレスポンスのため、レスポンスに含まれる計測IDを使用して、後続の処理に影響がないようにする
          measurementId = projectDetails.measures[0][this.destinationAPI][0].measurement_id;
        }
        const functionName = this.$t('individual.individual_diagnosis_function_active_test');
        const fileName = getMeasurementFileName(dataName, functionName, this.vehicleInfo.vehicle.vin.vin ?? '');

        csvWriter = new CsvFileWriter(fileName);
        await csvWriter.init();

        const exportCsvParams = {
          csvFileWriter: csvWriter,
          currentComponent: this,
          dataMonitorComponent: this.$refs.data_monitor,
          projectDetails,
          measurementId,
          storage: dmUtil.DATA_STORAGE.INTDASH,
          errorDialogCallback: () => { },
          updateExportProgress: this.updateExportProgress,
          isDebug: false,
        };
        await exportCsv(exportCsvParams);
      } catch (error) {
        if (CsvFileWriter.isUserCancel(error)) {
          // ユーザーがCSVの書き込みをキャンセルしたら何もせず終了
          return;
        }
        alert.showError(this, null, this.$t('message.common_systemError'), () => { });
      } finally {
        this.showCsvProgress = false;
        csvWriter && await csvWriter.close();
      }
    },

    /**
     * SP7は独自でisDrivingを持つため、独自のisDrivingフラグ変更した際のイベント処理
     * @param {boolean} isDrive
     */
    notifyChangeDrive(isDrive) {
      if (this.inExecution || isDrive) {
        // AT駆動中、またはDM実行中はツリーを表示しない
        this.setViewProcessMode(config.CONTAINER_EXECUTING);
      } else {
        // 両方実行中でなければ、ツリーを表示する
        this.setViewProcessMode(config.PROCESS_IDLING);
      }
    }
  },

  created() {
    // noop
  },

  mounted() {
    this.mountedLogic();
    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('keyup', this.onKeyUp);
  },

  beforeUnmount() {
    this.$refs.control_panel?.clear();
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('keyup', this.onKeyUp);
  }
};
